import React, { useMemo } from 'react';
import { ArrowUpRight, DollarSign, GitBranch } from 'lucide-react';
import { components, palette, typography } from '../../utils/designTokens';
import {
  formatCompactCurrency,
  formatCurrency,
  formatInteger,
  formatPercent,
  MIN_TOP_OFFENDER_TRACE_COUNT,
} from './costFormatters';

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const findHighestBy = (rows, metricKey, minimumTraceCount = 0) => {
  return rows
    .filter((row) => toNumber(row?.trace_count) >= minimumTraceCount)
    .reduce((winner, row) => {
      if (!winner || toNumber(row?.[metricKey]) > toNumber(winner?.[metricKey])) {
        return row;
      }
      return winner;
    }, null);
};

const buildOffenderData = (rows) => {
  const totalWinner = findHighestBy(rows, 'total_cost_usd');
  const avgWinner = findHighestBy(rows, 'avg_cost_per_trace_usd', MIN_TOP_OFFENDER_TRACE_COUNT);
  const sameWinner = totalWinner && avgWinner && totalWinner.key === avgWinner.key;

  return {
    totalWinner,
    avgWinner,
    sameWinner,
    hasQualifiedAvgWinner: Boolean(avgWinner),
  };
};

const SummaryCard = ({ icon: Icon, label, value, hint, detail, accent = palette.accents.primary.base, onClick, children }) => (
  <div
    className={`${components.card.bg} ${components.card.rounded} ${components.card.padding}`}
    style={{ border: `1px solid ${components.card.borderColor}`, cursor: onClick ? 'pointer' : undefined }}
    onClick={onClick}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div
          className={`${typography.size.metadata} ${typography.weight.semibold} uppercase tracking-[0.16em]`}
          style={{ color: palette.text.supporting }}
        >
          {label}
        </div>
        <div
          className={`${typography.size['2xl']} ${typography.weight.bold} mt-2`}
          style={{ color: palette.text.primary, lineHeight: 1.1 }}
        >
          {value}
        </div>
      </div>
      <div
        className="rounded-lg p-2"
        style={{
          backgroundColor: palette.neutrals.surfaceMid,
          color: accent,
          border: `1px solid ${accent}`,
        }}
      >
        <Icon size={16} />
      </div>
    </div>
    <div className="mt-3 flex flex-col gap-1.5">
      <div className={typography.size.sm} style={{ color: palette.text.supporting }}>
        {hint}
      </div>
      {detail ? (
        <div className={typography.size.label} style={{ color: palette.text.supporting }}>
          {detail}
        </div>
      ) : null}
      {children}
    </div>
  </div>
);

const WinnerBadge = ({ children, accent }) => (
  <span
    className={`${typography.size.label} ${typography.weight.semibold} inline-flex items-center rounded-full px-2 py-1`}
    style={{
      color: accent,
      backgroundColor: palette.neutrals.surfaceSubtle,
      border: `1px solid ${accent}`,
    }}
  >
    {children}
  </span>
);

const TopDimensionCard = ({ label, rows, accent, icon: Icon, onCardClick }) => {
  const offender = useMemo(() => buildOffenderData(rows), [rows]);

  if (rows.length === 0) {
    return (
      <SummaryCard
        icon={Icon}
        label={label}
        value="Unavailable"
        hint={`No ${label.toLowerCase()} cost data for this window.`}
        accent={accent}
      />
    );
  }

  const primaryWinner = offender.totalWinner || offender.avgWinner;

  return (
    <SummaryCard
      icon={Icon}
      label={label}
      value={primaryWinner?.label || '-'}
      hint={
        <span>
          <span className={typography.weight.semibold} style={{ color: palette.text.primary }}>
            {formatCompactCurrency(primaryWinner?.total_cost_usd)}
          </span>
          {` total spend across ${formatInteger(primaryWinner?.trace_count)} traces`}
        </span>
      }
      onClick={onCardClick && primaryWinner?.label ? () => onCardClick(primaryWinner.label) : undefined}
      detail={
        primaryWinner?.delta_cost_pct == null
          ? null
          : `${primaryWinner.delta_cost_pct >= 0 ? '+' : ''}${formatPercent(primaryWinner.delta_cost_pct, 2)} vs previous window`
      }
      accent={accent}
    >
      <div className="flex flex-wrap gap-2 pt-1">
        {offender.totalWinner ? <WinnerBadge accent={accent}>Highest total spend</WinnerBadge> : null}
        {offender.sameWinner ? <WinnerBadge accent={accent}>Highest avg / trace</WinnerBadge> : null}
      </div>

      {!offender.sameWinner && offender.avgWinner ? (
        <div className={`${typography.size.label} pt-1`} style={{ color: palette.text.supporting }}>
          Avg / trace leader: <span style={{ color: palette.text.primary }}>{offender.avgWinner.label}</span>{' '}
          at {formatCompactCurrency(offender.avgWinner.avg_cost_per_trace_usd)} across {formatInteger(offender.avgWinner.trace_count)} traces
        </div>
      ) : null}

      {!offender.hasQualifiedAvgWinner ? (
        <div className={`${typography.size.label} pt-1`} style={{ color: palette.text.supporting }}>
          No rows met the {formatInteger(MIN_TOP_OFFENDER_TRACE_COUNT)}-trace threshold for avg / trace.
        </div>
      ) : null}
    </SummaryCard>
  );
};

export default function CostSummaryPanel({
  summary,
  workflowRows,
  timeWindow,
  onDimensionClick,
}) {
  if (!summary || typeof summary !== 'object') {
    throw new Error('CostSummaryPanel requires a summary object');
  }
  if (!Array.isArray(workflowRows)) {
    throw new Error('CostSummaryPanel requires workflowRows array');
  }

  const deltaDirection = summary.delta_cost_usd == null || summary.delta_cost_usd === 0
    ? palette.text.muted
    : summary.delta_cost_usd > 0
      ? palette.status.warning
      : palette.status.success;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div
            className={`${typography.size.metadata} ${typography.weight.semibold} uppercase tracking-[0.16em]`}
            style={{ color: palette.text.supporting }}
          >
            Cost Summary
          </div>
          <p className={`${typography.size.sm} mt-2`} style={{ color: palette.text.supporting }}>
            Spend hotspots for the current {timeWindow} window.
          </p>
        </div>
        {summary.delta_cost_usd != null ? (
          <div className="flex items-center gap-1.5" style={{ color: deltaDirection }}>
            <ArrowUpRight size={13} style={{ transform: summary.delta_cost_usd < 0 ? 'rotate(90deg)' : 'none' }} />
            <span className={typography.size.label}>
              Total spend {summary.delta_cost_usd >= 0 ? '+' : ''}{formatCurrency(summary.delta_cost_usd)} vs previous window
            </span>
          </div>
        ) : null}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <SummaryCard
          icon={DollarSign}
          label="Total Spend"
          value={formatCompactCurrency(summary.total_cost_usd)}
          hint={`${formatInteger(summary.trace_count)} traces in the selected window`}
          detail={
            summary.previous_total_cost_usd == null
              ? 'No comparable previous window yet'
              : `Previous ${formatCurrency(summary.previous_total_cost_usd)} • ${formatPercent(summary.delta_cost_pct, 2)}`
          }
          accent={palette.accents.warm.base}
        />
        <SummaryCard
          icon={DollarSign}
          label="Avg Cost / Trace"
          value={formatCompactCurrency(summary.avg_cost_per_trace_usd)}
          hint={`Across ${formatInteger(summary.trace_count)} traces`}
          detail="Selected window average"
          accent={palette.accents.primary.base}
        />
        <TopDimensionCard
          icon={GitBranch}
          label="Top Business Task"
          rows={workflowRows}
          accent={palette.accents.secondary.base}
          onCardClick={onDimensionClick ? (value) => onDimensionClick('workflow', value) : undefined}
        />
      </div>
    </div>
  );
}
